import { Box, Button, Card, CardActions, CardContent, CardHeader, Chip, Divider, Grid, IconButton, List, ListItem, ListItemText, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useUser } from "../../context/userContext";
import { TrophyIcon, Eye, EyeSlash } from "@phosphor-icons/react";
import { UpdatePasswordForm } from "./updatePasswordForm";
import UpdateUserDetailsForm from "./updateUserDetailsForm";
import { useCallback, useEffect, useState, useRef } from "react";
import { getAgentDetailsByAgentId, getCommissionHistory, uploadProfilePhoto } from "../../api/userApi";
import { getMySlips } from '../../api/commissionSlipApi';
import { generateInvitation, getMyInvitations } from '../../api/invitationApi';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';
import { downloadMyCp58 } from '../../api/cp58Api';
import { generateNamecard, getSalesTitle } from '../../api/formGeneratorApi';
import api from '../../api/axiosInstance';
import TierCard from './tierCard';
import { getTiers } from '../../api/tierApi';
import handleAgentName from '../../utils/userUtils';

const getInitials = (name) => {
    if (!name) return '?';
    return name.substring(0, 2).toUpperCase();
};

export function UserProfile() {
    const { user } = useUser();

    const userRoles = Array.isArray(user?.roles) ? user.roles : [];
    const userGroup = user?.groupName || "";
    // Viewer-only users are not part of the sales chain — hide Tier,
    // Commission, and Rate History cards on their profile. If they hold
    // any earner role alongside Viewer, the cards stay visible.
    const isViewerOnly = userRoles.length > 0 && userRoles.every(r => r === 'Viewer');

    const [userDetails, setUserDetails] = useState();
    const [mySlips, setMySlips] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [commHistory, setCommHistory] = useState([]);
    const [tiers, setTiers] = useState([]);
    // Strict-history availability flags (same source the sidebar uses) so the
    // Commission card only shows chips for types the user has actually earned.
    // Default everything to false until the call returns; PSC always shows.
    const [commAvail, setCommAvail] = useState({ hasPsc: true, hasDoc: false, hasToc: false, hasMoc: false });
    const [generating, setGenerating] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [showComm, setShowComm] = useState(false);
    const [taxYear, setTaxYear] = useState(new Date().getFullYear() - 1);
    const [downloadingCp58, setDownloadingCp58] = useState(false);
    const [namecardBusy, setNamecardBusy] = useState(false);
    const [salesTitle, setSalesTitle] = useState('');

    useEffect(() => {
        if (!user?.userId) return;
        getSalesTitle(user.userId).then(r => setSalesTitle(r.data?.title || '')).catch(() => {});
    }, [user?.userId]);

    const handleDownloadNamecard = async () => {
        if (!user?.userId || namecardBusy) return;
        setNamecardBusy(true);
        try {
            await generateNamecard(user.userId);
        } catch (e) {
            alert(`Namecard download failed: ${e?.message || e}`);
        } finally {
            setNamecardBusy(false);
        }
    };
    const fileInputRef = useRef(null);
    const apiBase = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

    const fetchAgentDetails = async () => {
        if (!user?.userId) return;
        try {
            const response = await getAgentDetailsByAgentId(user.userId);
            setUserDetails(response.data);
        }
        catch (error) {
            console.error("Error fetching agent details:", error);
        }
    };

    const [invitationsLoaded, setInvitationsLoaded] = useState(false);

    const fetchInvitations = useCallback(async () => {
        if (!user?.userId) return;
        try {
            const res = await getMyInvitations(user.userId);
            setInvitations(res.data || []);
        } catch {}
        setInvitationsLoaded(true);
    }, [user?.userId]);

    useEffect(() => {
        fetchAgentDetails();
        getMySlips().then(res => setMySlips(res.data)).catch(() => {});
        fetchInvitations();
        if (user?.userId) getCommissionHistory(user.userId).then(res => setCommHistory(res.data || [])).catch(() => {});
        getTiers().then(r => setTiers(r.data || [])).catch(() => {});
        if (user?.userId) api.get('/CommissionProgress/GetMyCommAvailability').then(r => setCommAvail(r.data || {})).catch(() => {});
    }, [user?.userId]);

    const generateForRole = async (role) => {
        try {
            await generateInvitation({ inviterId: user.userId, intendedRole: role });
        } catch (err) {
            console.error(`Failed to generate ${role} link:`, err);
        }
    };

    // Regenerate fresh links for both Sales Agent and Referral. Old links
    // stay in the table (status flips to expired/used over time) so
    // application counts remain attributed to this user no matter which
    // historical token an applicant came through.
    const regenerateBoth = async () => {
        if (!user?.userId) return;
        setGenerating(true);
        await generateForRole('Sales Agent');
        await generateForRole('Referral');
        await fetchInvitations();
        setGenerating(false);
    };

    // Auto-create one Active link per role on first profile open so the user
    // never sees an empty state. Runs once after invitations first load.
    const autoGenChecked = useRef(false);
    useEffect(() => {
        if (autoGenChecked.current || !invitationsLoaded || !user?.userId) return;
        autoGenChecked.current = true;
        const hasSales = invitations.some(i => i.status === 'Active' && i.intendedRole === 'Sales Agent');
        const hasRef   = invitations.some(i => i.status === 'Active' && i.intendedRole === 'Referral');
        if (hasSales && hasRef) return;
        (async () => {
            setGenerating(true);
            if (!hasSales) await generateForRole('Sales Agent');
            if (!hasRef)   await generateForRole('Referral');
            await fetchInvitations();
            setGenerating(false);
        })();
    }, [invitationsLoaded, invitations, user?.userId]);

    const getLink = (token) => `${window.location.origin}/join/${token}`;

    const copyLink = async (token, id) => {
        try { await navigator.clipboard.writeText(getLink(token)); }
        catch {
            const ta = document.createElement('textarea');
            ta.value = getLink(token); ta.style.cssText = 'position:fixed;opacity:0';
            document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
        }
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return alert('File size must be under 5MB');
        try {
            const res = await uploadProfilePhoto(file);
            const photoUrl = res.data.url;
            // Backend auto-saves to user profile, just update UI
            localStorage.setItem("ProfilePhotoUrl", photoUrl);
            setUserDetails(prev => ({ ...prev, profilePhotoUrl: photoUrl }));
        } catch (err) {
            alert('Upload failed: ' + (err.response?.data || err.message));
        }
    };

    // Profile shows ONLY PSC. DOC/TOC/MOC are deliberately omitted because
    // their effective rate varies per recipient/relationship (e.g. each
    // downline may carry a different DOC %), so showing a single number is
    // misleading. Sidebar Commission menu still gates DOC/TOC/MOC by
    // historical receipt — that surface is action-oriented (drill into the
    // detail page) so the menu link is fine, just not a top-level rate chip.
    const commission = [
        { id: 'PSC', name: 'Personal Sales Commission', percentage: (userDetails?.personalComm ?? 0) + '%' },
    ];

    return (
        <Box sx={{ pt: 2, pb: 4, width: '100%' }}>
            <Stack spacing={3}>
                {/* <Box sx={{ mt: 8, mb: 2 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                            fontWeight: 'bold'
                        }}
                    >
                        User Profile
                    </Typography>
                </Box> */}
                <Grid container spacing={3}>
                    {/* Left Column: User Info & Commission */}
                    <Grid size={{ lg: 4, md: 6, xs: 12 }}>
                        <Stack spacing={3}>
                            {/* Card 1: User Info */}
                            <Card>
                                <CardContent>
                                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                        <div
                                            onClick={() => fileInputRef.current?.click()}
                                            style={{
                                                width: 72, height: 72, borderRadius: 14,
                                                background: 'linear-gradient(135deg, #1A8CFF, #6366F1)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: 'white', fontSize: 24, fontWeight: 700, fontFamily: "'Poppins', sans-serif",
                                                cursor: 'pointer', position: 'relative', overflow: 'hidden'
                                            }}
                                            title="Click to upload photo"
                                        >
                                            {(userDetails?.profilePhotoUrl || user?.profilePhotoUrl) ? (
                                                <img src={`${apiBase}${userDetails?.profilePhotoUrl || user?.profilePhotoUrl}`} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : getInitials(user?.username)}
                                        </div>
                                        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handlePhotoUpload} />
                                        <Stack spacing={0.5}>
                                            {/* Main heading — real name, fall back to username */}
                                            <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#0F172A', fontFamily: "'Poppins', sans-serif", lineHeight: 1.2 }}>
                                                {handleAgentName(userDetails?.firstName, userDetails?.lastName, userDetails?.alias) || user?.username}
                                            </Typography>
                                            {/* Sub-line: @username · group */}
                                            <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif" }}>
                                                @{user?.username}{(userGroup || userDetails?.groupName) ? ` · ${userGroup || userDetails?.groupName}` : ''}
                                            </Typography>
                                            {/* Sales title (display only) */}
                                            {salesTitle && (
                                                <Typography sx={{ fontWeight: 700, color: '#0F62CC', fontSize: '0.85rem', fontFamily: "'Poppins', sans-serif" }}>
                                                    {salesTitle}
                                                </Typography>
                                            )}
                                            {/* Role chips */}
                                            {userRoles.length > 0 && (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                                    {userRoles.map(role => (
                                                        <Chip
                                                            key={role}
                                                            label={role}
                                                            size="small"
                                                            sx={{ backgroundColor: '#EEF2F7', color: '#475569', fontSize: '0.68rem', height: 22, fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </Stack>
                                    </Stack>
                                </CardContent>
                                <Divider />
                                <CardActions>
                                    <Button fullWidth variant="text" disabled>
                                        Upload picture
                                    </Button>
                                </CardActions>
                            </Card>

                            {/* Sales Kit — downloadable marketing assets (personalised name card, …) */}
                            <Card>
                                <CardHeader
                                    title="Sales Kit"
                                    titleTypographyProps={{ variant: 'h6' }}
                                    subheader="Your personalised marketing assets"
                                />
                                <Divider />
                                <CardContent>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>Name Card</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', textTransform: 'none', mb: 1.5 }}>
                                        Your personalised PDF business card — name, title, phone &amp; email.
                                    </Typography>
                                    <Button fullWidth variant="contained" size="small" disabled={namecardBusy} onClick={handleDownloadNamecard}>
                                        {namecardBusy ? 'Generating…' : 'Download Name Card (PDF)'}
                                    </Button>
                                </CardContent>
                            </Card>

                            {/* Tier card — current rank + revenue progress (Sales Agents only) */}
                            {!isViewerOnly && (
                                <Box sx={{ mb: 2 }}>
                                    <TierCard />
                                </Box>
                            )}

                            {/* Card 2: Commission */}
                            {!isViewerOnly && (
                            <Card>
                                <CardHeader
                                    avatar={<TrophyIcon size={20} color="#000000" />}
                                    title="Commission"
                                    titleTypographyProps={{ variant: 'h6' }}
                                    action={
                                        <IconButton size="small" onClick={() => setShowComm(v => !v)} sx={{ mt: 0.5 }}>
                                            {showComm ? <Eye size={18} /> : <EyeSlash size={18} />}
                                        </IconButton>
                                    }
                                />
                                <Divider />
                                <List>
                                    {commission.map((item, index) => (
                                        <ListItem
                                            divider={index < commission.length - 1}
                                            key={item.id}
                                            secondaryAction={
                                                <Typography variant="h6" sx={{ color: '#4636f5' }}>
                                                    {showComm ? item.percentage : '****'}
                                                </Typography>
                                            }
                                        >
                                            <ListItemText
                                                primary={item.id}
                                                primaryTypographyProps={{ variant: 'subtitle2', color: 'text.primary' }}
                                                secondary={item.name}
                                                secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Card>
                            )}

                            {/* Rate History */}
                            {!isViewerOnly && commHistory.length > 0 && (
                                <Card>
                                    <CardHeader
                                        title="Rate History"
                                        titleTypographyProps={{ variant: 'h6' }}
                                        subheader="Commission rate changes over time"
                                    />
                                    <Divider />
                                    <CardContent sx={{ p: 0 }}>
                                        {commHistory.map((h, i) => {
                                            // Resolve tier from PSC% so every row shows consistent label.
                                            const tier = tiers.find(t => Number(t.pscPercent) === Number(h.personalComm));
                                            const tierLabel = tier ? `Promotion to ${tier.name}` : 'Commission rate updated';
                                            return (
                                            <Box key={h.historyId || i} sx={{ px: 2, py: 1.5, borderBottom: i < commHistory.length - 1 ? '1px solid #F1F5F9' : 'none', '&:hover': { backgroundColor: '#F8FAFC' } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                                        {/* Profile only shows PSC — DOC/TOC/MOC vary per relationship and
                                                            would be misleading as a single fixed rate here. */}
                                                        <Chip label={showComm ? `PSC ${h.personalComm ?? 0}%` : 'PSC ****'} size="small" sx={{ fontWeight: 600, fontSize: '0.68rem', fontFamily: "'Poppins', sans-serif", backgroundColor: '#DBEAFE', color: '#1E40AF' }} />
                                                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                                                            {tierLabel}
                                                        </Typography>
                                                    </Box>
                                                    {!h.effectiveTo && <Chip label="Current" size="small" sx={{ fontWeight: 600, fontSize: '0.62rem', backgroundColor: '#10B981', color: 'white' }} />}
                                                </Box>
                                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: "'Poppins', sans-serif", mt: 0.3 }}>
                                                    {new Date(h.effectiveFrom).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    {h.effectiveTo ? ` — ${new Date(h.effectiveTo).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })}` : ' — Present'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif" }}>
                                                    Changed by {h.changedByName || h.changedBy}
                                                </Typography>
                                            </Box>
                                            );
                                        })}
                                    </CardContent>
                                </Card>
                            )}
                        </Stack>
                    </Grid>

                    {/* Right Column: Edit Profile & Update Password */}
                    <Grid size={{ lg: 8, md: 6, xs: 12 }}>
                        <Stack spacing={3}>
                            {/* Form 1: Edit Profile */}
                            <Card>
                                <CardContent>
                                    <UpdateUserDetailsForm
                                        userDetails={userDetails}
                                        setUserDetails={setUserDetails}
                                    />
                                </CardContent>
                            </Card>
                            {/* <form onSubmit={(e) => e.preventDefault()}>
                                <Card>
                                    <CardHeader subheader="The information can be edited" title="Profile" />
                                    <Divider />
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            <Grid item md={12} xs={12}>
                                                <FormControl fullWidth required>
                                                    <InputLabel>Name</InputLabel>
                                                    <OutlinedInput defaultValue={user.name} label="Name" name="name" />
                                                </FormControl>
                                            </Grid>
                                            <Grid item md={6} xs={12}>
                                                <FormControl fullWidth required>
                                                    <InputLabel>Email address</InputLabel>
                                                    <OutlinedInput defaultValue={user.email} label="Email address" name="email" />
                                                </FormControl>
                                            </Grid>
                                            <Grid item md={6} xs={12}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Phone number</InputLabel>
                                                    <OutlinedInput defaultValue={user.phone} label="Phone number" name="phone" type="tel" />
                                                </FormControl>
                                            </Grid>
                                            <Grid item md={12} xs={12}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Address</InputLabel>
                                                    <OutlinedInput defaultValue={user.address} label="Address" name="address" />
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                    <Divider />
                                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <Button variant="contained">Save</Button>
                                    </CardActions>
                                </Card>
                            </form> */}

                            {/* Form 2: Update Password */}
                            <Card>
                                <CardContent>
                                    <UpdatePasswordForm />
                                </CardContent>
                            </Card>

                            {/* Tax Documents (CP58) — Viewer is non-earner, no CP58 */}
                            {!isViewerOnly && (
                            <Card>
                                <CardHeader
                                    title="Tax Documents (CP58)"
                                    subheader="Download your yearly LHDN CP58 statement"
                                    titleTypographyProps={{ variant: 'h6' }}
                                />
                                <Divider />
                                <CardContent>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Select
                                            value={taxYear}
                                            onChange={e => setTaxYear(Number(e.target.value))}
                                            size="small"
                                            sx={{ minWidth: 110 }}
                                        >
                                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                                <MenuItem key={y} value={y}>{y}</MenuItem>
                                            ))}
                                        </Select>
                                        <Button
                                            variant="contained"
                                            disabled={downloadingCp58}
                                            onClick={async () => {
                                                setDownloadingCp58(true);
                                                try {
                                                    await downloadMyCp58(taxYear);
                                                } catch (err) {
                                                    alert('Failed to download CP58: ' + (err.message || 'Unknown error'));
                                                } finally {
                                                    setDownloadingCp58(false);
                                                }
                                            }}
                                            sx={{
                                                background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                                                color: 'white',
                                                fontFamily: "'Poppins', sans-serif",
                                                fontWeight: 600,
                                                fontSize: '0.78rem',
                                                textTransform: 'none',
                                                borderRadius: '7px',
                                                boxShadow: 'none',
                                                px: 2,
                                                '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
                                            }}
                                        >
                                            {downloadingCp58 ? 'Generating...' : 'Download CP58'}
                                        </Button>
                                    </Stack>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif", mt: 1.5 }}>
                                        Sums approved commission, extra, and cash incentive payouts in the chosen calendar year.
                                    </Typography>
                                </CardContent>
                            </Card>
                            )}

                            {/* Agent Invitation — two fixed sections: Sales Agent + Referral.
                                Links auto-generate on first profile open so the user never
                                sees an empty state. "Regenerate Links" makes fresh ones for
                                both roles (old links keep their application counts).
                                Viewer is non-earner, no recruiting → hide for Viewer-only. */}
                            {!isViewerOnly && (
                            <Card>
                                <CardHeader
                                    title="Agent Invitation"
                                    subheader="Share invitation links to recruit new Sales Agents and Referrals"
                                    titleTypographyProps={{ variant: 'h6' }}
                                    action={
                                        <Button size="small" onClick={regenerateBoth} disabled={generating} sx={{
                                            background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', color: 'white', fontFamily: "'Poppins', sans-serif",
                                            fontWeight: 600, fontSize: '0.72rem', textTransform: 'none', borderRadius: '7px', boxShadow: 'none', px: 1.5,
                                            '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
                                        }}>
                                            {generating ? 'Working...' : 'Regenerate Links'}
                                        </Button>
                                    }
                                />
                                <Divider />
                                <CardContent>
                                    <Stack spacing={2}>
                                        {['Sales Agent', 'Referral'].map(role => {
                                            const roleInvs = invitations.filter(i => i.intendedRole === role);
                                            const sorted = [...roleInvs].sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
                                            const primary = sorted.find(i => i.status === 'Active') || sorted[0];
                                            const apps = roleInvs.reduce((s, i) => s + (i.applicationCount || 0), 0);
                                            const approved = roleInvs.reduce((s, i) => s + (i.approvedCount || 0), 0);
                                            const pending = roleInvs.reduce((s, i) => s + (i.pendingCount || 0), 0);
                                            const rejected = roleInvs.reduce((s, i) => s + (i.rejectedCount || 0), 0);
                                            return (
                                                <Box key={role} sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px', p: 1.5, border: '1px solid #E2E8F0' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                                                                {role}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
                                                                {apps} application{apps !== 1 ? 's' : ''} all-time
                                                                {apps > 0 && ` · ${approved} approved · ${pending} pending · ${rejected} rejected`}
                                                            </Typography>
                                                        </Box>
                                                        {primary && (
                                                            <Button size="small" onClick={() => copyLink(primary.token, primary.invitationId)} sx={{
                                                                fontFamily: "'Poppins', sans-serif", fontSize: '0.68rem', fontWeight: 600, textTransform: 'none',
                                                                borderRadius: '6px', minWidth: 0, px: 1.5,
                                                                backgroundColor: copiedId === primary.invitationId ? '#D1FAE5' : '#EFF6FF',
                                                                color: copiedId === primary.invitationId ? '#065F46' : '#1A8CFF',
                                                            }}>
                                                                {copiedId === primary.invitationId ? 'Copied!' : 'Copy Link'}
                                                            </Button>
                                                        )}
                                                    </Box>
                                                    {primary ? (
                                                        <Typography sx={{ fontSize: '0.72rem', color: '#374151', fontFamily: "'Poppins', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {getLink(primary.token)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif" }}>
                                                            {generating ? 'Generating…' : 'No active link.'}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </CardContent>
                            </Card>
                            )}
                            {/* Commission Slips — Viewer is non-earner, no slips */}
                            {!isViewerOnly && mySlips.length > 0 && (
                                <Card>
                                    <CardHeader
                                        title="Commission Slips"
                                        subheader="Your approved commission payment slips"
                                        titleTypographyProps={{ variant: 'h6' }}
                                    />
                                    <Divider />
                                    <CardContent sx={{ p: 0 }}>
                                        {mySlips.map((slip, i) => (
                                            <Box key={slip.slipId} sx={{
                                                px: 2, py: 1.5,
                                                borderBottom: i < mySlips.length - 1 ? '1px solid #F1F5F9' : 'none',
                                                opacity: slip.status === 'Voided' ? 0.6 : 1,
                                                '&:hover': { backgroundColor: '#F8FAFC' }
                                            }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                                                            {slip.period}
                                                        </Typography>
                                                        {slip.status === 'Voided' && (
                                                            <Chip label="Voided" size="small" sx={{
                                                                backgroundColor: '#FEE2E2', color: '#991B1B',
                                                                fontSize: '0.62rem', fontWeight: 600, height: 20,
                                                                fontFamily: "'Poppins', sans-serif"
                                                            }} />
                                                        )}
                                                        {slip.status === 'Approved' && (
                                                            <Chip label="Approved" size="small" sx={{
                                                                backgroundColor: '#D1FAE5', color: '#065F46',
                                                                fontSize: '0.62rem', fontWeight: 600, height: 20,
                                                                fontFamily: "'Poppins', sans-serif"
                                                            }} />
                                                        )}
                                                    </Box>
                                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: slip.status === 'Voided' ? '#94A3B8' : '#1A8CFF', fontFamily: "'Poppins', sans-serif" }}>
                                                        RM {new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2 }).format(slip.totalPayout)}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
                                                    Issued {new Date(slip.dateIssued).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </Typography>
                                                {slip.status === 'Voided' && slip.voidedReason && (
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#DC2626', fontStyle: 'italic', fontFamily: "'Poppins', sans-serif", mt: 0.3 }}>
                                                        {slip.voidedReason}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Stack>

        </Box>
    );
}
